import { useTranslation } from 'react-i18next';
import type { QrCodeAuditAction } from '../api/types';

const ROW_PADDING = 16;
const LABEL_HEIGHT = 17;
const LINE_HEIGHT = 19;
const SIDE_GAP = 6;
const DEFAULT_SUFFIX = ' (default)';

interface Line {
  language: string;
  value: string;
}

function linesOf(action: QrCodeAuditAction, value: string): Line[] {
  if (action !== 'LanguagesChanged' || !value.includes(': ')) {
    return [{ language: '', value }];
  }

  return value.split('; ').map((item) => {
    const separator = item.indexOf(': ');
    const rest = item.slice(separator + 2);
    const isDefault = rest.endsWith(DEFAULT_SUFFIX);
    return {
      language: `${item.slice(0, separator)}${isDefault ? '*' : ''}`,
      value: isDefault ? rest.slice(0, -DEFAULT_SUFFIX.length) : rest,
    };
  });
}

function sidesOf(action: QrCodeAuditAction, before: string | null, after: string | null) {
  return [
    { key: 'before', value: before },
    { key: 'after', value: after },
  ]
    .filter((side): side is { key: string; value: string } => Boolean(side.value))
    .map((side) => ({ key: side.key, lines: linesOf(action, side.value) }));
}

export function auditChangeHeight(action: QrCodeAuditAction, before: string | null, after: string | null): number | undefined {
  const sides = sidesOf(action, before, after);
  if (sides.length === 0) {
    return undefined;
  }

  const lines = sides.reduce((total, side) => total + side.lines.length, 0);
  return ROW_PADDING + sides.length * LABEL_HEIGHT + (sides.length - 1) * SIDE_GAP + lines * LINE_HEIGHT;
}

export function AuditChange({
  action,
  before,
  after,
}: {
  action: QrCodeAuditAction;
  before: string | null;
  after: string | null;
}) {
  const { t } = useTranslation();
  const sides = sidesOf(action, before, after);

  if (sides.length === 0) {
    return null;
  }

  return (
    <div className="audit-change">
      {sides.map((side) => (
        <div key={side.key} className="audit-change__side">
          <span className="audit-change__label">{t(`qrCode.audit.${side.key}`)}</span>
          <ul className="audit-change__list">
            {side.lines.map((line) => (
              <li key={`${line.language}${line.value}`} title={line.value} className={line.language ? 'has-language' : undefined}>
                {line.language && <span className="mono">{line.language}</span>}
                <span className="audit-change__value">{line.value}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
